import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { AuthService, CurrentUser } from 'src/app/auth.service';
import { ContactAutocompleteResult, ContactAutocompleteService } from 'src/app/services/contacts/contact-autocomplete.service';

type RateContactType = 'carrier' | 'customer';
type RateManagerTab = 'rates' | 'editor' | 'import' | 'json';
type ThemeMode = 'dark' | 'light';
type ReadinessTone = 'idle' | 'ready' | 'warning' | 'error';

interface RateRow {
  [key: string]: unknown;
}

interface RateZone {
  uuid: string;
  label: string;
  raw: Record<string, unknown>;
}

interface RateListResult {
  rows: RateRow[];
  total: number;
}

interface RateManagerIssue {
  severity: 'error' | 'warning';
  message: string;
}

interface RateOption {
  label: string;
  value: string;
  carrierOnly?: boolean;
  customerOnly?: boolean;
}

@Component({
  selector: 'app-rate-manager',
  templateUrl: './rate-manager.component.html',
  styleUrl: './rate-manager.component.scss'
})
export class RateManagerComponent implements OnInit, OnDestroy {
  private readonly apiUrl = '/api/rates/rate_manager.php';
  private readonly destroy$ = new Subject<void>();
  private readonly themeStorageKey = 'rate-manager-theme';

  readonly rateMethodOptions: RateOption[] = [
    { label: 'Weight', value: '1' },
    { label: 'Linear feet', value: '2' },
    { label: 'Cubic weight', value: '3' },
    { label: 'Cubic volume', value: '4', customerOnly: true }
  ];

  readonly rateUnitOptions: RateOption[] = [
    { label: 'lbs', value: '1' },
    { label: 'kg', value: '2' },
    { label: 'ft', value: '3' },
    { label: 'm', value: '4' },
    { label: 'per 100 lbs', value: '5', carrierOnly: true }
  ];

  readonly currencyOptions = ['USD', 'CAD'];
  readonly deliveryTypeOptions = [
    { label: 'Type 1', value: '1' },
    { label: 'Type 2', value: '2' }
  ];

  contactType: RateContactType = 'carrier';
  selectedContact: ContactAutocompleteResult | null = null;
  contactOptionsOpen = false;
  contactSearchControl = new FormControl('', { nonNullable: true });
  contactOptions$: Observable<ContactAutocompleteResult[]> = of([]);

  filterForm: UntypedFormGroup = this.fb.group({
    active: ['true'],
    effective_on: [''],
    rate_method: [''],
    rate_unit: [''],
    delivery_type: [''],
    origin_zone_uuid: [''],
    destination_zone_uuid: [''],
    currency: [''],
    limit: [100]
  });

  editorForm: UntypedFormGroup = this.fb.group({
    rate_uuid: [''],
    start_date: ['', Validators.required],
    end_date: [''],
    rate_method: ['1', Validators.required],
    rate_unit: ['1', Validators.required],
    origin_zone_uuid: ['', Validators.required],
    destination_zone_uuid: ['', Validators.required],
    length_start: [''],
    length_end: [''],
    weight_start: [''],
    weight_end: [''],
    delivery_type: [''],
    transit_days: [''],
    is_rate_active: [true],
    rate: ['', Validators.required],
    min_rate: [''],
    max_rate: [''],
    linear_feet_overage_threshold: [''],
    linear_feet_overage_weight: [''],
    currency: ['USD', Validators.required]
  });

  rates: RateRow[] = [];
  zones: RateZone[] = [];
  originZones: RateZone[] = [];
  destinationZones: RateZone[] = [];
  selectedRate: RateRow | null = null;
  totalRows = 0;
  offset = 0;
  activeTab: RateManagerTab = 'rates';
  themeMode: ThemeMode = 'dark';
  sidePanelCollapsed = false;
  errorMessage = '';
  pageNote = '';
  csvText = '';
  importFileName = '';
  importPreview: unknown = null;
  importCommitResult: unknown = null;
  lastResponse: unknown = null;
  permissions = new Set<string>();

  loading = {
    zones: false,
    rates: false,
    detail: false,
    save: false,
    delete: false,
    importPreview: false,
    importCommit: false
  };

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly http: HttpClient,
    private readonly contactAutocomplete: ContactAutocompleteService,
    private readonly toastr: ToastrService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeTheme();
    this.setupContactSearch();
    this.loadCurrentUserPermissions();
    this.resetEditorForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedContactUuid(): string {
    return this.selectedContact?.value ?? '';
  }

  get selectedContactName(): string {
    return this.selectedContact?.label ?? 'No contact selected';
  }

  get selectedRateUuid(): string {
    return this.selectedRate ? this.rateUuid(this.selectedRate) : String(this.editorForm.get('rate_uuid')?.value ?? '');
  }

  get selectedTypeLabel(): string {
    return this.contactType === 'carrier' ? 'Carrier' : 'Customer';
  }

  get contactSearchPlaceholder(): string {
    return this.contactType === 'carrier' ? 'Search carrier contact' : 'Search customer contact';
  }

  get canView(): boolean {
    return this.hasPermission('glebus_rate_manager_view') || this.hasPermission('rates.manage');
  }

  get canEdit(): boolean {
    return this.hasPermission('glebus_rate_manager_edit') || this.hasPermission('rates.manage');
  }

  get canImport(): boolean {
    return this.hasPermission('glebus_rate_manager_import') || this.hasPermission('rates.manage');
  }

  get canDelete(): boolean {
    return this.hasPermission('glebus_rate_manager_delete') || this.hasPermission('rates.manage');
  }

  get canSaveRate(): boolean {
    return this.canEdit && !!this.selectedContactUuid && this.editorForm.valid && !this.loading.save;
  }

  get canPreviewImport(): boolean {
    return this.canImport && !!this.selectedContactUuid && this.csvText.trim().length > 0 && !this.loading.importPreview;
  }

  get canCommitImport(): boolean {
    return this.canImport && !!this.selectedContactUuid && this.csvText.trim().length > 0 && !this.loading.importCommit;
  }

  get activeRatesCount(): number {
    return this.rates.filter((row) => this.isRateActive(row)).length;
  }

  get inactiveRatesCount(): number {
    return this.rates.length - this.activeRatesCount;
  }

  get currentLimit(): number {
    const limit = Number(this.filterForm.get('limit')?.value ?? 100);
    return Number.isFinite(limit) && limit > 0 ? limit : 100;
  }

  get pageStart(): number {
    return this.rates.length ? this.offset + 1 : 0;
  }

  get pageEnd(): number {
    return this.offset + this.rates.length;
  }

  get canPageBack(): boolean {
    return this.offset > 0 && !this.loading.rates;
  }

  get canPageForward(): boolean {
    if (this.loading.rates) {
      return false;
    }

    if (this.totalRows > 0) {
      return this.offset + this.rates.length < this.totalRows;
    }

    return this.rates.length >= this.currentLimit;
  }

  get readinessLabel(): string {
    if (this.errorMessage) {
      return 'Blocked';
    }

    if (!this.selectedContactUuid) {
      return 'Contact Needed';
    }

    if (this.loading.rates || this.loading.zones) {
      return 'Loading';
    }

    if (!this.zones.length) {
      return 'No Zones';
    }

    return 'Ready';
  }

  get readinessTone(): ReadinessTone {
    if (this.errorMessage) {
      return 'error';
    }

    if (!this.selectedContactUuid || !this.zones.length) {
      return 'warning';
    }

    return 'ready';
  }

  get readinessPercent(): number {
    const steps = [
      !!this.selectedContactUuid,
      this.zones.length > 0,
      !this.loading.rates && !this.loading.zones,
      !this.errorMessage
    ];

    return Math.round((steps.filter(Boolean).length / steps.length) * 100);
  }

  get submitDisabledReason(): string {
    if (!this.selectedContactUuid) {
      return 'Select a contact.';
    }

    if (this.errorMessage) {
      return this.errorMessage;
    }

    if (!this.zones.length && !this.loading.zones) {
      return 'No zones returned for this contact.';
    }

    return `${this.rates.length} rates loaded for ${this.selectedTypeLabel.toLowerCase()}.`;
  }

  get issueList(): RateManagerIssue[] {
    const issues: RateManagerIssue[] = [];

    if (this.errorMessage) {
      issues.push({ severity: 'error', message: this.errorMessage });
    }

    if (!this.selectedContactUuid) {
      issues.push({ severity: 'warning', message: 'Select a carrier or customer contact.' });
    }

    if (this.selectedContactUuid && !this.loading.zones && !this.zones.length) {
      issues.push({ severity: 'warning', message: 'No zones are available for the selected contact.' });
    }

    return issues;
  }

  get visibleIssues(): RateManagerIssue[] {
    return this.issueList.slice(0, 6);
  }

  get lastResponseJson(): string {
    return this.formatJson(this.lastResponse);
  }

  get selectedRateJson(): string {
    return this.formatJson(this.selectedRate);
  }

  get importPreviewJson(): string {
    return this.formatJson(this.importPreview);
  }

  get importCommitJson(): string {
    return this.formatJson(this.importCommitResult);
  }

  get contactTypeSearchName(): string {
    return this.contactType === 'carrier' ? 'carrier_contact_list' : 'customer_contact_list';
  }

  setContactType(type: RateContactType): void {
    if (this.contactType === type) {
      return;
    }

    this.contactType = type;
    this.selectedContact = null;
    this.contactSearchControl.setValue('');
    this.contactOptionsOpen = false;
    this.rates = [];
    this.sidePanelCollapsed = false;
    this.zones = [];
    this.originZones = [];
    this.destinationZones = [];
    this.offset = 0;
    this.totalRows = 0;
    this.selectedRate = null;
    this.sidePanelCollapsed = false;
    this.errorMessage = '';
    this.importPreview = null;
    this.importCommitResult = null;
    this.filterForm.patchValue({ origin_zone_uuid: '', destination_zone_uuid: '' });
    this.resetEditorForm();
  }

  onContactSelected(contact: ContactAutocompleteResult): void {
    this.selectedContact = contact;
    this.contactSearchControl.setValue(contact.label, { emitEvent: false });
    this.contactOptionsOpen = false;
    this.offset = 0;
    this.selectedRate = null;
    this.errorMessage = '';
    this.importPreview = null;
    this.importCommitResult = null;
    this.filterForm.patchValue({ origin_zone_uuid: '', destination_zone_uuid: '' });
    this.resetEditorForm();
    this.loadZones();
    this.loadRates();
  }

  closeContactOptionsSoon(): void {
    window.setTimeout(() => {
      this.contactOptionsOpen = false;
    }, 160);
  }

  toggleSidePanel(): void {
    this.sidePanelCollapsed = !this.sidePanelCollapsed;
  }

  showSidePanel(): void {
    this.sidePanelCollapsed = false;
  }

  loadAll(): void {
    if (!this.selectedContactUuid) {
      this.toastr.warning('Select a contact first.');
      return;
    }

    this.loadZones();
    this.loadRates();
  }

  loadZones(): void {
    if (!this.selectedContactUuid) {
      return;
    }

    this.loading.zones = true;
    this.errorMessage = '';

    this.http.get<unknown>(this.apiUrl, {
      params: this.baseParams('zones')
    }).pipe(
      finalize(() => {
        this.loading.zones = false;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.lastResponse = response;
        const lists = this.extractZoneLists(response);
        this.originZones = lists.origin;
        this.destinationZones = lists.destination;
        this.zones = this.uniqueZones([...lists.origin, ...lists.destination]);
      },
      error: (error: HttpErrorResponse) => this.handleApiError(error, 'Unable to load zones.')
    });
  }

  loadRates(resetOffset = false): void {
    if (!this.selectedContactUuid) {
      this.toastr.warning('Select a contact first.');
      return;
    }

    if (resetOffset) {
      this.offset = 0;
    }

    this.loading.rates = true;
    this.errorMessage = '';

    this.http.get<unknown>(this.apiUrl, {
      params: this.rateListParams()
    }).pipe(
      finalize(() => {
        this.loading.rates = false;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.lastResponse = response;
        const result = this.extractRateList(response);
        this.rates = result.rows;
        this.totalRows = result.total;
        this.pageNote = this.rates.length ? 'Rates loaded.' : 'No rates matched.';
        if (this.rates.length) {
          this.sidePanelCollapsed = true;
        }
      },
      error: (error: HttpErrorResponse) => this.handleApiError(error, 'Unable to load freight rates.')
    });
  }

  resetFilters(): void {
    this.filterForm.reset({
      active: 'true',
      effective_on: '',
      rate_method: '',
      rate_unit: '',
      delivery_type: '',
      origin_zone_uuid: '',
      destination_zone_uuid: '',
      currency: '',
      limit: 100
    });
    this.loadRates(true);
  }

  pageBack(): void {
    if (!this.canPageBack) {
      return;
    }

    this.offset = Math.max(0, this.offset - this.currentLimit);
    this.loadRates();
  }

  pageForward(): void {
    if (!this.canPageForward) {
      return;
    }

    this.offset += this.currentLimit;
    this.loadRates();
  }

  selectRate(row: RateRow): void {
    this.selectedRate = row;
    this.patchEditorFromRate(row);
    this.activeTab = 'editor';

    const uuid = this.rateUuid(row);
    if (!uuid) {
      return;
    }

    this.loading.detail = true;
    this.http.get<unknown>(this.apiUrl, {
      params: this.baseParams('freight-rates').set('rate_uuid', uuid)
    }).pipe(
      finalize(() => {
        this.loading.detail = false;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.lastResponse = response;
        const detail = this.extractRateDetail(response);
        if (detail) {
          this.selectedRate = detail;
          this.patchEditorFromRate(detail);
        }
      },
      error: () => {
        this.loading.detail = false;
      }
    });
  }

  newRate(): void {
    this.selectedRate = null;
    this.resetEditorForm();
    this.activeTab = 'editor';
  }

  saveRate(): void {
    if (!this.selectedContactUuid) {
      this.toastr.warning('Select a contact first.');
      return;
    }

    if (!this.canEdit) {
      this.toastr.error('Missing rate manager edit permission.');
      return;
    }

    if (this.editorForm.invalid) {
      this.editorForm.markAllAsTouched();
      this.toastr.warning('Complete the required rate fields.');
      return;
    }

    const rateUuid = String(this.editorForm.get('rate_uuid')?.value ?? '').trim();
    const method = rateUuid ? 'PUT' : 'POST';
    let params = this.baseParams('freight-rates');

    if (rateUuid) {
      params = params.set('rate_uuid', rateUuid);
    }

    this.loading.save = true;
    this.http.request<unknown>(method, this.apiUrl, {
      params,
      body: this.buildRatePayload(rateUuid)
    }).pipe(
      finalize(() => {
        this.loading.save = false;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.lastResponse = response;
        this.toastr.success(rateUuid ? 'Rate updated.' : 'Rate created.');
        this.activeTab = 'rates';
        this.loadRates();
      },
      error: (error: HttpErrorResponse) => this.handleApiError(error, 'Unable to save rate.')
    });
  }

  deactivateRate(row: RateRow | null = this.selectedRate): void {
    if (!row) {
      this.toastr.warning('Select a rate first.');
      return;
    }

    if (!this.canDelete) {
      this.toastr.error('Missing rate manager delete permission.');
      return;
    }

    const uuid = this.rateUuid(row);
    if (!uuid) {
      this.toastr.error('Selected rate does not have a rate UUID.');
      return;
    }

    if (!window.confirm('Deactivate this rate?')) {
      return;
    }

    this.loading.delete = true;
    this.http.delete<unknown>(this.apiUrl, {
      params: this.baseParams('freight-rates').set('rate_uuid', uuid)
    }).pipe(
      finalize(() => {
        this.loading.delete = false;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.lastResponse = response;
        this.toastr.success('Rate deactivated.');
        this.selectedRate = null;
        this.resetEditorForm();
        this.loadRates();
      },
      error: (error: HttpErrorResponse) => this.handleApiError(error, 'Unable to deactivate rate.')
    });
  }

  previewImport(): void {
    this.sendImport('import-preview');
  }

  commitImport(): void {
    this.sendImport('import-commit');
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.importFileName = file.name;
      this.csvText = String(reader.result ?? '');
      this.importPreview = null;
      this.importCommitResult = null;
    };
    reader.onerror = () => {
      this.toastr.error('Unable to read import file.');
    };
    reader.readAsText(file);
  }

  setActiveTab(tab: RateManagerTab): void {
    this.activeTab = tab;
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Dark' : 'Light';
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-moon' : 'fa-solid fa-sun';
  }

  readinessClass(): string {
    return `rm-readiness--${this.readinessTone}`;
  }

  issueClass(issue: RateManagerIssue): string {
    return issue.severity === 'error' ? 'rm-issue--error' : 'rm-issue--warning';
  }

  isMethodAvailable(option: RateOption): boolean {
    if (option.customerOnly) {
      return this.contactType === 'customer';
    }

    if (option.carrierOnly) {
      return this.contactType === 'carrier';
    }

    return true;
  }

  isUnitAvailable(option: RateOption): boolean {
    return this.isMethodAvailable(option);
  }

  methodLabel(value: unknown): string {
    const option = this.rateMethodOptions.find((item) => item.value === String(value ?? ''));
    return option?.label ?? this.emptyDash(value);
  }

  unitLabel(value: unknown): string {
    const option = this.rateUnitOptions.find((item) => item.value === String(value ?? ''));
    return option?.label ?? this.emptyDash(value);
  }

  activeLabel(row: RateRow): string {
    return this.isRateActive(row) ? 'Active' : 'Inactive';
  }

  statusClass(row: RateRow): string {
    return this.isRateActive(row) ? 'is-success' : 'is-muted';
  }

  rateUuid(row: RateRow): string {
    return this.firstString(row, ['carrier_zone_rate_uuid', 'rate_uuid', 'uuid']);
  }

  originZoneLabel(row: RateRow): string {
    const direct = this.firstString(row, [
      'origin_zone_name',
      'origin_carrier_zone_name',
      'origin_customer_zone_name',
      'origin_name'
    ]);

    if (direct) {
      return direct;
    }

    return this.zoneLabelByUuid(this.firstString(row, ['origin_carrier_zone_uuid', 'origin_zone_uuid']));
  }

  destinationZoneLabel(row: RateRow): string {
    const direct = this.firstString(row, [
      'destination_zone_name',
      'destination_carrier_zone_name',
      'destination_customer_zone_name',
      'destination_name'
    ]);

    if (direct) {
      return direct;
    }

    return this.zoneLabelByUuid(this.firstString(row, ['destination_carrier_zone_uuid', 'destination_zone_uuid']));
  }

  formatDate(value: unknown): string {
    const text = String(value ?? '').trim();
    if (!text) {
      return '-';
    }

    return text.includes('T') ? text.slice(0, 10) : text;
  }

  rateAmount(row: RateRow): string {
    return this.formatMoneyFromCents(this.firstValue(row, ['rate']), this.firstString(row, ['currency']) || 'USD');
    return this.formatMoney(this.firstValue(row, ['rate']), this.firstString(row, ['currency']) || 'USD');
  }

  minMaxAmount(row: RateRow): string {
    const currency = this.firstString(row, ['currency']) || 'USD';
    const min = this.firstValue(row, ['min_rate']);
    const max = this.firstValue(row, ['max_rate']);

    if (this.isBlank(min) && this.isBlank(max)) {
      return '-';
    }

    return `${this.formatMoneyFromCents(min, currency)} / ${this.formatMoneyFromCents(max, currency)}`;
  }

  weightRange(row: RateRow): string {
    return this.rangeLabel(row, 'weight');
  }

  lengthRange(row: RateRow): string {
    return this.rangeLabel(row, 'length');
  }

  transitLabel(row: RateRow): string {
    return this.emptyDash(this.firstValue(row, ['transit_days', 'transit_time']));
  }

  deliveryTypeLabel(row: RateRow): string {
    const value = this.firstString(row, ['delivery_type']);
    if (!value) {
      return '-';
    }

    return this.deliveryTypeOptions.find((option) => option.value === value)?.label ?? ('Type ' + value);
  }

  trackByRate = (index: number, row: RateRow): string => {
    return this.rateUuid(row) || `rate-row-${index}`;
  };

  trackByZone = (_: number, zone: RateZone): string => {
    return zone.uuid;
  };

  trackByIssue = (index: number, issue: RateManagerIssue): string => {
    return `${issue.severity}-${issue.message}-${index}`;
  };

  private setupContactSearch(): void {
    this.contactOptions$ = this.contactSearchControl.valueChanges.pipe(
      startWith(''),
      map((value) => String(value ?? '').trim()),
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((query) => {
        if (query.length < 3) {
          return of([]);
        }

        return this.contactAutocomplete.search(this.contactTypeSearchName, query).pipe(
          catchError(() => of([]))
        );
      }),
      takeUntil(this.destroy$)
    );
  }

  private loadCurrentUserPermissions(): void {
    this.authService.ensureCurrentUserLoaded().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (currentUser: CurrentUser | null) => {
        this.permissions = new Set(currentUser?.permissions ?? []);
      },
      error: () => {
        this.permissions = new Set<string>();
      }
    });
  }

  private initializeTheme(): void {
    const stored = localStorage.getItem(this.themeStorageKey);
    this.themeMode = stored === 'light' ? 'light' : 'dark';
  }

  private hasPermission(permission: string): boolean {
    return this.permissions.has(permission);
  }

  private baseParams(resource: string): HttpParams {
    return new HttpParams()
      .set('type', this.contactType)
      .set('contact_uuid', this.selectedContactUuid)
      .set('resource', resource);
  }

  private rateListParams(): HttpParams {
    let params = this.baseParams('freight-rates')
      .set('limit', String(this.currentLimit))
      .set('offset', String(this.offset));

    const raw = this.filterForm.getRawValue() as Record<string, unknown>;
    const filterKeys = [
      'active',
      'effective_on',
      'rate_method',
      'rate_unit',
      'delivery_type',
      'origin_zone_uuid',
      'destination_zone_uuid',
      'currency'
    ];

    for (const key of filterKeys) {
      const value = raw[key];
      if (!this.isBlank(value)) {
        params = params.set(key, String(value));
      }
    }

    return params;
  }

  private resetEditorForm(): void {
    this.editorForm.reset({
      rate_uuid: '',
      start_date: this.today(),
      end_date: '',
      rate_method: '1',
      rate_unit: '1',
      origin_zone_uuid: '',
      destination_zone_uuid: '',
      length_start: '',
      length_end: '',
      weight_start: '',
      weight_end: '',
      delivery_type: '',
      transit_days: '',
      is_rate_active: true,
      rate: '',
      min_rate: '',
      max_rate: '',
      linear_feet_overage_threshold: '',
      linear_feet_overage_weight: '',
      currency: 'USD'
    });
  }

  private patchEditorFromRate(row: RateRow): void {
    this.editorForm.patchValue({
      rate_uuid: this.rateUuid(row),
      start_date: this.formatDateForInput(this.firstValue(row, ['start_date'])),
      end_date: this.formatDateForInput(this.firstValue(row, ['end_date'])),
      rate_method: this.firstString(row, ['rate_method']) || '1',
      rate_unit: this.firstString(row, ['rate_unit']) || '1',
      origin_zone_uuid: this.firstString(row, ['origin_carrier_zone_uuid', 'origin_zone_uuid']),
      destination_zone_uuid: this.firstString(row, ['destination_carrier_zone_uuid', 'destination_zone_uuid']),
      length_start: this.emptyInput(this.firstValue(row, ['length_start'])),
      length_end: this.emptyInput(this.firstValue(row, ['length_end'])),
      weight_start: this.emptyInput(this.firstValue(row, ['weight_start'])),
      weight_end: this.emptyInput(this.firstValue(row, ['weight_end'])),
      delivery_type: this.emptyInput(this.firstValue(row, ['delivery_type'])),
      transit_days: this.emptyInput(this.firstValue(row, ['transit_days', 'transit_time'])),
      is_rate_active: this.toBoolean(this.firstValue(row, ['is_rate_active'])),
      rate: this.emptyInput(this.firstValue(row, ['rate'])),
      min_rate: this.emptyMoneyInputFromCents(this.firstValue(row, ['min_rate'])),
      max_rate: this.emptyMoneyInputFromCents(this.firstValue(row, ['max_rate'])),
      linear_feet_overage_threshold: this.emptyInput(this.firstValue(row, ['linear_feet_overage_threshold'])),
      linear_feet_overage_weight: this.emptyInput(this.firstValue(row, ['linear_feet_overage_weight'])),
      currency: this.firstString(row, ['currency']) || 'USD'
    });
  }

  private buildRatePayload(rateUuid: string): Record<string, unknown> {
    const raw = this.editorForm.getRawValue() as Record<string, unknown>;
    const payload: Record<string, unknown> = {
      start_date: this.nullableText(raw['start_date']),
      end_date: this.nullableText(raw['end_date']),
      rate_method: this.nullableNumber(raw['rate_method']),
      rate_unit: this.nullableNumber(raw['rate_unit']),
      length_start: this.nullableNumber(raw['length_start']),
      length_end: this.nullableNumber(raw['length_end']),
      weight_start: this.nullableNumber(raw['weight_start']),
      weight_end: this.nullableNumber(raw['weight_end']),
      delivery_type: this.nullableNumber(raw['delivery_type']),
      is_rate_active: this.toBoolean(raw['is_rate_active']),
      rate: this.nullableNumber(raw['rate']),
      min_rate: this.nullableCents(raw['min_rate']),
      max_rate: this.nullableCents(raw['max_rate']),
      linear_feet_overage_threshold: this.nullableNumber(raw['linear_feet_overage_threshold']),
      linear_feet_overage_weight: this.nullableNumber(raw['linear_feet_overage_weight']),
      currency: this.nullableText(raw['currency']) || 'USD'
    };

    if (this.contactType === 'carrier') {
      payload['carrier_contact_uuid'] = this.selectedContactUuid;
      payload['origin_carrier_zone_uuid'] = this.nullableText(raw['origin_zone_uuid']);
      payload['destination_carrier_zone_uuid'] = this.nullableText(raw['destination_zone_uuid']);
      payload['transit_time'] = this.nullableNumber(raw['transit_days']);

      if (rateUuid) {
        payload['carrier_zone_rate_uuid'] = rateUuid;
      }
    } else {
      payload['customer_uuid'] = this.selectedContactUuid;
      payload['origin_zone_uuid'] = this.nullableText(raw['origin_zone_uuid']);
      payload['destination_zone_uuid'] = this.nullableText(raw['destination_zone_uuid']);
      payload['transit_days'] = this.nullableNumber(raw['transit_days']);

      if (rateUuid) {
        payload['rate_uuid'] = rateUuid;
      }
    }

    return payload;
  }

  private sendImport(resource: 'import-preview' | 'import-commit'): void {
    if (!this.selectedContactUuid) {
      this.toastr.warning('Select a contact first.');
      return;
    }

    if (!this.canImport) {
      this.toastr.error('Missing rate manager import permission.');
      return;
    }

    const body = this.buildImportBody();
    if (!body) {
      this.toastr.warning('Add CSV text or upload a file first.');
      return;
    }

    const loadingKey = resource === 'import-preview' ? 'importPreview' : 'importCommit';
    this.loading[loadingKey] = true;

    this.http.post<unknown>(this.apiUrl, body, {
      params: this.baseParams(resource)
    }).pipe(
      finalize(() => {
        this.loading[loadingKey] = false;
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        this.lastResponse = response;
        if (resource === 'import-preview') {
          this.importPreview = response;
          this.toastr.success('Import preview complete.');
        } else {
          this.importCommitResult = response;
          this.toastr.success('Import committed.');
          this.loadRates(true);
        }
      },
      error: (error: HttpErrorResponse) => this.handleApiError(error, resource === 'import-preview' ? 'Unable to preview import.' : 'Unable to commit import.')
    });
  }

  private buildImportBody(): Record<string, unknown> | null {
    const text = this.csvText.trim();
    if (!text) {
      return null;
    }

    if (text.startsWith('{') || text.startsWith('[')) {
      try {
        const parsed = JSON.parse(text) as unknown;
        if (Array.isArray(parsed)) {
          return { rows: parsed };
        }

        const parsedRecord = this.asRecord(parsed);
        if (Array.isArray(parsedRecord['rows']) || typeof parsedRecord['csv'] === 'string' || typeof parsedRecord['csv_base64'] === 'string') {
          return parsedRecord;
        }
      } catch {
        return { csv: text };
      }
    }

    return { csv: text };
  }

  private extractZoneLists(response: unknown): { origin: RateZone[]; destination: RateZone[] } {
    const payload = this.unwrapData(response);
    const originRaw = this.findArrayByKeys(payload, ['origin_zones', 'originZones', 'origins']);
    const destinationRaw = this.findArrayByKeys(payload, ['destination_zones', 'destinationZones', 'destinations']);
    const commonRaw = this.findFirstArray(payload, ['zones', 'rows', 'items', 'data']);
    const origin = originRaw.length ? originRaw : commonRaw;
    const destination = destinationRaw.length ? destinationRaw : commonRaw;

    return {
      origin: this.normalizeZoneList(origin),
      destination: this.normalizeZoneList(destination)
    };
  }

  private extractRateList(response: unknown): RateListResult {
    const payload = this.unwrapData(response);
    const rows = this.findFirstArray(payload, ['rows', 'rates', 'freight_rates', 'items', 'data'])
      .map((item) => this.asRecord(item))
      .filter((row) => Object.keys(row).length > 0);
    const responseRecord = this.asRecord(response);
    const totalRaw = this.firstDeep(payload, ['total', 'total_rows', 'count', 'recordsTotal'])
      ?? this.firstDeep(responseRecord['meta'], ['total', 'total_rows', 'count', 'recordsTotal']);
    const total = Number(totalRaw);

    return {
      rows: [...rows],
      total: Number.isFinite(total) && total >= rows.length ? total : rows.length
    };
  }

  private extractRateDetail(response: unknown): RateRow | null {
    const payload = this.unwrapData(response);

    if (Array.isArray(payload)) {
      return this.asRecord(payload[0]);
    }

    const record = this.asRecord(payload);
    const nested = this.asRecord(record['rate'] ?? record['row'] ?? record['data']);
    return Object.keys(nested).length ? nested : record;
  }

  private normalizeZoneList(rows: unknown[]): RateZone[] {
    const zones = rows
      .map((row) => this.normalizeZone(row))
      .filter((zone): zone is RateZone => zone !== null);

    return this.uniqueZones(zones).sort((first, second) => first.label.localeCompare(second.label));
  }

  private normalizeZone(row: unknown): RateZone | null {
    const record = this.asRecord(row);
    const uuid = this.firstString(record, [
      'zone_uuid',
      'carrier_zone_uuid',
      'customer_zone_uuid',
      'default_zone_uuid',
      'origin_zone_uuid',
      'destination_zone_uuid',
      'origin_carrier_zone_uuid',
      'destination_carrier_zone_uuid',
      'uuid'
    ]);

    if (!uuid) {
      return null;
    }

    const label = this.firstString(record, [
      'zone_name',
      'carrier_zone_name',
      'customer_zone_name',
      'name',
      'label',
      'description'
    ]) || this.cityRegionLabel(record) || this.shortUuid(uuid);

    return { uuid, label, raw: record };
  }

  private uniqueZones(zones: RateZone[]): RateZone[] {
    const seen = new Map<string, RateZone>();

    for (const zone of zones) {
      if (!seen.has(zone.uuid)) {
        seen.set(zone.uuid, zone);
      }
    }

    return Array.from(seen.values());
  }

  private zoneLabelByUuid(uuid: string): string {
    if (!uuid) {
      return '-';
    }

    const zone = this.zones.find((item) => item.uuid === uuid)
      ?? this.originZones.find((item) => item.uuid === uuid)
      ?? this.destinationZones.find((item) => item.uuid === uuid);

    return zone?.label ?? this.shortUuid(uuid);
  }

  private cityRegionLabel(record: Record<string, unknown>): string {
    const city = this.firstString(record, ['city', 'locality', 'zone_city']);
    const region = this.firstString(record, ['region', 'state', 'province', 'rc']);

    if (city && region) {
      return `${city}, ${region}`;
    }

    return city || region;
  }

  private isRateActive(row: RateRow): boolean {
    return this.toBoolean(this.firstValue(row, ['is_rate_active', 'active']));
  }

  private rangeLabel(row: RateRow, prefix: 'weight' | 'length'): string {
    const start = this.firstString(row, [`${prefix}_start`]);
    const end = this.firstString(row, [`${prefix}_end`]);

    if (!start && !end) {
      return '-';
    }

    return `${start || '0'} - ${end || 'open'}`;
  }

  private formatMoney(value: unknown, currency: string): string {
    if (this.isBlank(value)) {
      return '-';
    }

    const amount = Number(value);
    if (!Number.isFinite(amount)) {
      return String(value);
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'CAD' ? 'CAD' : 'USD',
      maximumFractionDigits: 4
    }).format(amount);
  }

  private formatMoneyFromCents(value: unknown, currency: string): string {
    if (this.isBlank(value)) {
      return '-';
    }

    const cents = Number(value);
    if (!Number.isFinite(cents)) {
      return String(value);
    }

    return this.formatMoney(cents / 100, currency);
  }

  private emptyMoneyInputFromCents(value: unknown): string {
    if (this.isBlank(value)) {
      return '';
    }

    const cents = Number(value);
    if (!Number.isFinite(cents)) {
      return String(value);
    }

    return (cents / 100).toFixed(2);
  }

  private nullableCents(value: unknown): number | null {
    if (this.isBlank(value)) {
      return null;
    }

    const amount = Number(value);
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  }

  private emptyDash(value: unknown): string {
    return this.isBlank(value) ? '-' : String(value);
  }

  private emptyInput(value: unknown): string {
    return this.isBlank(value) ? '' : String(value);
  }

  private nullableText(value: unknown): string | null {
    const text = String(value ?? '').trim();
    return text ? text : null;
  }

  private nullableNumber(value: unknown): number | null {
    if (this.isBlank(value)) {
      return null;
    }

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private isBlank(value: unknown): boolean {
    return value === null || value === undefined || String(value).trim() === '';
  }

  private toBoolean(value: unknown): boolean {
    return value === true
      || value === 1
      || value === '1'
      || value === 't'
      || value === 'true'
      || value === 'TRUE';
  }

  private today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private formatDateForInput(value: unknown): string {
    const text = String(value ?? '').trim();
    if (!text) {
      return '';
    }

    return text.slice(0, 10);
  }

  private shortUuid(uuid: string): string {
    return uuid.length > 12 ? `${uuid.slice(0, 8)}...${uuid.slice(-4)}` : uuid;
  }

  private firstValue(source: unknown, keys: string[]): unknown {
    const record = this.asRecord(source);

    for (const key of keys) {
      const value = record[key];
      if (!this.isBlank(value)) {
        return value;
      }
    }

    return undefined;
  }

  private firstString(source: unknown, keys: string[]): string {
    const value = this.firstValue(source, keys);
    return this.isBlank(value) ? '' : String(value).trim();
  }

  private firstDeep(source: unknown, keys: string[]): unknown {
    const record = this.asRecord(source);

    for (const key of keys) {
      const value = record[key];
      if (!this.isBlank(value)) {
        return value;
      }
    }

    const data = record['data'];
    if (data && data !== source && typeof data === 'object') {
      return this.firstDeep(data, keys);
    }

    return undefined;
  }

  private unwrapData(response: unknown): unknown {
    const record = this.asRecord(response);

    if (Object.prototype.hasOwnProperty.call(record, 'data')) {
      return record['data'];
    }

    return response;
  }

  private findArrayByKeys(source: unknown, keys: string[]): unknown[] {
    const record = this.asRecord(source);

    for (const key of keys) {
      const value = record[key];
      if (Array.isArray(value)) {
        return value;
      }
    }

    const data = record['data'];
    if (data && data !== source && typeof data === 'object') {
      return this.findArrayByKeys(data, keys);
    }

    return [];
  }

  private findFirstArray(source: unknown, keys: string[]): unknown[] {
    if (Array.isArray(source)) {
      return source;
    }

    const keyed = this.findArrayByKeys(source, keys);
    return keyed.length ? keyed : [];
  }

  private asRecord(value: unknown): Record<string, unknown> {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }

    return {};
  }

  private formatJson(value: unknown): string {
    if (value === null || value === undefined) {
      return '{}';
    }

    return JSON.stringify(value, null, 2);
  }

  private handleApiError(error: HttpErrorResponse, fallback: string): void {
    const message = this.apiErrorMessage(error, fallback);
    this.errorMessage = message;
    this.toastr.error(message);
  }

  private apiErrorMessage(error: HttpErrorResponse, fallback: string): string {
    const errorBody = this.asRecord(error.error);
    const message = errorBody['message'] ?? errorBody['error'];

    if (typeof message === 'string' && message.trim()) {
      return message;
    }

    if (typeof error.error === 'string' && error.error.trim()) {
      return error.error;
    }

    return error.status ? `${fallback} (${error.status})` : fallback;
  }
}
